import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Leaf,
  Sun,
  Droplets,
  Lightbulb,
  Zap,
} from 'lucide-react';
import { Loader2 } from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';
import {
  useGetSamagamGreenBuildingRainwaterQuery,
  useGetSamagamGreenBuildingSolarQuery,
  useGetSamagamGreenBuildingLEDQuery,
  useGetSamagamGreenBuildingSolarLightsQuery,
  type GreenBuildingCategoryResponse,
} from '../../services/kvsSamagamApi';

// Merged table row — one entry per RO across all 4 categories
interface MergedRegionRow {
  Sn: number;
  roID: number;
  roName: string;
  rainwater: number;
  solar: number;
  led: number;
  solarLights: number;
}



// Merge four already-aggregated RO responses into one table row per region
function mergeRegionRows(
  rainData: GreenBuildingCategoryResponse | null | undefined,
  solarData: GreenBuildingCategoryResponse | null | undefined,
  ledData: GreenBuildingCategoryResponse | null | undefined,
  slData: GreenBuildingCategoryResponse | null | undefined
): MergedRegionRow[] {
  const roMap = new Map<number, string>();
  [rainData, solarData, ledData, slData].forEach(d =>
    d?.data.forEach(r => roMap.set(r.roID, r.roName))
  );
  const sorted = Array.from(roMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  return sorted.map(([roID, roName], idx) => ({
    Sn: idx + 1,
    roID,
    roName,
    rainwater:   rainData?.data.find(r => r.roID === roID)?.total ?? 0,
    solar:       solarData?.data.find(r => r.roID === roID)?.total ?? 0,
    led:         ledData?.data.find(r => r.roID === roID)?.total ?? 0,
    solarLights: slData?.data.find(r => r.roID === roID)?.total ?? 0,
  }));
}

export default function GreenBuildingAnalysis() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'rainwater' | 'solar' | 'led' | 'solar-lights'>('rainwater');
  
  const { data: rainData } = useGetSamagamGreenBuildingRainwaterQuery();
  const { data: solarData } = useGetSamagamGreenBuildingSolarQuery();
  const { data: ledData } = useGetSamagamGreenBuildingLEDQuery();
  const { data: slData } = useGetSamagamGreenBuildingSolarLightsQuery();

  const isLoading = !rainData || !solarData || !ledData || !slData;

  // Merge all 4 category responses into one region table
  const regions = useMemo(() => mergeRegionRows(rainData, solarData, ledData, slData), [rainData, solarData, ledData, slData]);


  const tabs = [
    { id: 'rainwater',    label: 'Rainwater Harvesting',        icon: Droplets },
    { id: 'solar',        label: 'Solar Power Plant',           icon: Sun },
    { id: 'led',          label: 'Efficient Electric System',    icon: Lightbulb },
    { id: 'solar-lights', label: 'Solar Lights',                icon: Zap },
  ];

  const getActiveValue = (r: MergedRegionRow) =>
    activeTab === 'rainwater' ? r.rainwater :
    activeTab === 'solar'     ? r.solar :
    activeTab === 'led'       ? r.led :
    r.solarLights;

  const chartData = regions.map(r => ({
    category:         r.roName,
    pmshriCount:      getActiveValue(r),
    nonPmshriCount:   0,
    total:            getActiveValue(r),
    pmshriPercentage: 0,
  }));

  const pieData = regions.map(r => ({ name: r.roName, value: getActiveValue(r) }));

  const activeTabLabel = tabs.find(t => t.id === activeTab)?.label ?? '';

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Leaf className="w-7 h-7" />
              Green Building Analysis
            </h1>
            <p className="text-gray-600">
              Region-wise rainwater harvesting, solar power, efficient electric systems and solar lights across all KVs
            </p>
          </div>
          <div className="flex items-center gap-2 mt-2 md:mt-0">
            {isLoading && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
            <button
              onClick={() => navigate('/kvs-samagam/analytics')}
              className="flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Analytics
            </button>
          </div>
        </div>


        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Rainwater Harvesting"
            value={(rainData?.summary.total ?? 0).toLocaleString()}
            icon={<Droplets className="w-6 h-6" />}
            color="blue"
            subtitle={`PM-SHRI: ${rainData?.summary.pmshri ?? 0} | Non PM-SHRI: ${rainData?.summary.nonpmshri ?? 0}`}
          />
          <StatCard
            title="Solar Power Plant"
            value={(solarData?.summary.total ?? 0).toLocaleString()}
            icon={<Sun className="w-6 h-6" />}
            color="orange"
            subtitle={`PM-SHRI: ${solarData?.summary.pmshri ?? 0} | Non PM-SHRI: ${solarData?.summary.nonpmshri ?? 0}`}
          />
          <StatCard
            title="Efficient Electric System"
            value={(ledData?.summary.total ?? 0).toLocaleString()}
            icon={<Lightbulb className="w-6 h-6" />}
            color="green"
            subtitle={`PM-SHRI: ${ledData?.summary.pmshri ?? 0} | Non PM-SHRI: ${ledData?.summary.nonpmshri ?? 0}`}
          />
          <StatCard
            title="Solar Lights"
            value={(slData?.summary.total ?? 0).toLocaleString()}
            icon={<Zap className="w-6 h-6" />}
            color="teal"
            subtitle={`PM-SHRI: ${slData?.summary.pmshri ?? 0} | Non PM-SHRI: ${slData?.summary.nonpmshri ?? 0}`}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-purple-700 border-purple-600 bg-purple-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">{activeTabLabel} — by Region</h3>
            <div className="overflow-x-auto">
              <div style={{ minWidth: `${regions.length * 60}px`, width: '100%' }}>
                <ComparisonBarChart
                  data={chartData}
                  title=""
                  xAxisLabel="Region"
                />
              </div>
            </div>
          </div>
          <DistributionPieChart
            data={pieData}
            title={`${activeTabLabel} — Region Distribution`}
          />
        </div>

        {/* Region-wise Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Region-wise Green Building Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-center py-3 px-3 font-semibold text-gray-600 w-10">Sn</th>
                  <th className="text-left   py-3 px-4 font-semibold text-gray-700">Region Name</th>
                  <th className="text-center py-3 px-3 font-semibold text-blue-700">
                    <div className="flex flex-col items-center">
                      <Droplets className="w-4 h-4 mb-0.5" />
                      Rainwater Harvesting
                    </div>
                  </th>
                  <th className="text-center py-3 px-3 font-semibold text-orange-700">
                    <div className="flex flex-col items-center">
                      <Sun className="w-4 h-4 mb-0.5" />
                      Solar Power Plant
                    </div>
                  </th>
                  <th className="text-center py-3 px-3 font-semibold text-green-700">
                    <div className="flex flex-col items-center">
                      <Lightbulb className="w-4 h-4 mb-0.5" />
                      Efficient Electric System
                    </div>
                  </th>
                  <th className="text-center py-3 px-3 font-semibold text-teal-700">
                    <div className="flex flex-col items-center">
                      <Zap className="w-4 h-4 mb-0.5" />
                      Solar Lights
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {regions.map((row) => {
                  return (
                    <tr key={row.roID} className="hover:bg-gray-50">
                      <td className="py-3 px-3 text-center text-gray-400 font-medium">{row.Sn}</td>
                      <td className="py-3 px-4 font-semibold text-gray-800">{row.roName}</td>
                      <td className="py-3 px-3 text-center text-blue-700 font-medium">{row.rainwater}</td>
                      <td className="py-3 px-3 text-center text-orange-700 font-medium">{row.solar}</td>
                      <td className="py-3 px-3 text-center text-green-700 font-medium">{row.led}</td>
                      <td className="py-3 px-3 text-center text-teal-700 font-medium">{row.solarLights}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                  <td className="py-3 px-3 text-center text-gray-500" colSpan={2}>Total</td>
                  <td className="py-3 px-3 text-center text-blue-700">{rainData?.summary.total ?? 0}</td>
                  <td className="py-3 px-3 text-center text-orange-700">{solarData?.summary.total ?? 0}</td>
                  <td className="py-3 px-3 text-center text-green-700">{ledData?.summary.total ?? 0}</td>
                  <td className="py-3 px-3 text-center text-teal-700">{slData?.summary.total ?? 0}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure',   icon: '🏫', color: 'blue' },
              { name: 'Enrollment',     path: '/kvs-samagam/analytics/enrollment',        icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile',    path: '/kvs-samagam/analytics/ict-profile',       icon: '💻', color: 'purple' },
              { name: 'Library Profile',path: '/kvs-samagam/analytics/library-profile',   icon: '📚', color: 'orange' },
              { name: 'CWSN Profile',   path: '/kvs-samagam/analytics/cwsn-profile',      icon: '♿', color: 'pink' },
              { name: 'NCC Profile',    path: '/kvs-samagam/analytics/ncc-profile',       icon: '🎖️', color: 'cyan' },
              { name: 'Sports',         path: '/kvs-samagam/analytics/sports',            icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building',   icon: '🌿', color: 'teal' },
              { name: 'Safety',         path: '/kvs-samagam/analytics/safety',            icon: '🛡️', color: 'red' },
              { name: 'Special Labs',   path: '/kvs-samagam/analytics/special-labs',     icon: '🔬', color: 'indigo' },
              { name: 'Water Profile',  path: '/kvs-samagam/analytics/miscellaneous',      icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'Green Building';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
